import type { ReactNode } from "react";
import { Gift, type LucideIcon } from "lucide-react";

import Text from "@/blueprint/components/Text/Text";
import { PRESS_SCALE_CLASSES } from "@/blueprint/tokens/Motion";
import VisuallyHidden from "@/blueprint/components/VisuallyHidden/VisuallyHidden";
import { cn } from "@/lib/utils";

/*
 * The option card the configurators are built from: the shop's packages and
 * add-ons.
 */

export const PriceWithStrike = ({
  current,
  original,
}: {
  current: string;
  original: string;
}) => (
  <span className="inline-flex flex-col items-end whitespace-nowrap">
    <Text
      variant="paragraph-sm"
      tone="secondary"
      as="span"
      className="line-through [text-decoration-skip-ink:none]"
    >
      <span aria-hidden="true">{original}</span>
    </Text>
    <VisuallyHidden>{`was ${original}, now ${current}`}</VisuallyHidden>
    <Text
      variant="paragraph-md"
      as="span"
      className="font-semibold text-brand-black"
    >
      <span aria-hidden="true">{current}</span>
    </Text>
  </span>
);

export const optionCardClassName = (
  isSelected: boolean,
  isUnavailable: boolean,
) =>
  cn(
    // The press scale confirms the tap; colour and press share one transition.
    "flex w-full flex-col gap-xs rounded-s border bg-brand-white p-l text-left transition-[color,background-color,border-color,box-shadow,transform] duration-quick ease-out-strong motion-reduce:transition-none",
    !isUnavailable && PRESS_SCALE_CLASSES,
    isSelected
      ? "border-brand-black ring-1 ring-inset ring-brand-black"
      : isUnavailable
        ? "border-tint-black-20"
        : // Hover only where there is one, so a tap doesn't leave it stuck.
          "border-tint-black-20 [@media(hover:hover)]:hover:border-tint-black-70",
    isUnavailable && "cursor-not-allowed opacity-50",
  );

/** The id OptionCardBody gives its label, so a control can be named by it. */
export const optionLabelId = (descriptionId: string) =>
  `${descriptionId}-label`;

/** A note line; the object form opts one line into italics. */
export type OptionCardNote = string | { text: string; italic: true };

/** The label / notes / price body every option card shares. */
export const OptionCardBody = ({
  label,
  descriptionId,
  notes,
  price,
  priceIsStatus,
  media,
}: {
  label: string;
  descriptionId: string;
  notes: readonly OptionCardNote[];
  price: ReactNode;
  priceIsStatus: boolean;
  media: ReactNode;
}) => {
  const text = (
    <>
      <Text variant="paragraph-md" as="span" className="font-semibold">
        <span id={optionLabelId(descriptionId)}>{label}</span>
      </Text>
      <span
        id={descriptionId}
        className="flex w-full items-start justify-between gap-m"
      >
        <span className="flex min-w-0 flex-col">
          {notes.map((note) => {
            const text = typeof note === "string" ? note : note.text;
            return (
              <Text
                key={text}
                variant="paragraph-sm"
                tone="secondary"
                as="span"
                className={typeof note === "string" ? undefined : "italic"}
              >
                {text}
              </Text>
            );
          })}
        </span>
        {priceIsStatus ? (
          <Text
            variant="paragraph-sm"
            tone="secondary"
            as="span"
            className="ml-auto whitespace-nowrap text-right"
          >
            {price}
          </Text>
        ) : (
          <Text
            variant="paragraph-md"
            as="span"
            className="ml-auto shrink-0 whitespace-nowrap text-right font-semibold text-brand-black"
          >
            {price}
          </Text>
        )}
      </span>
    </>
  );

  if (media === null) {
    return text;
  }

  // The thumbnail leads the row and the label/notes column sits beside it, so
  // the card's own flex-col gap no longer separates them: repeat it here.
  return (
    <span className="flex w-full items-start gap-m">
      {media}
      <span className="flex min-w-0 flex-1 flex-col gap-xs">{text}</span>
    </span>
  );
};

/**
 * One hugging pill in two tones: master's bundle-bonus tag, which says up front
 * which package earns the free bag pass, and the sticky bar's trial note.
 *
 * A tone owns its glyph. Figma draws the gift as part of Label_Product and the
 * trial note has none, so the icon travels with the classes rather than being
 * passed in beside them — there is no such thing as a trial pill with a gift.
 */
type PillTone = { classes: string; icon?: LucideIcon };

const PILL_TONES: Record<"bonus" | "trial", PillTone> = {
  /**
   * Figma 9077:25556 (Label_Product): a fully rounded pill, 10px side padding
   * and 4px top and bottom, its 18px gift icon 2px before a SemiBold label,
   * with icon and label sharing one colour on a subtle ground. Both colours
   * and the tag's tracking are tokens, since raw hex and arbitrary tracking
   * are both banned under src/views.
   */
  bonus: {
    classes:
      "rounded-full bg-label-bonus-subtle px-2.5 py-1 font-semibold tracking-product-tag text-label-bonus",
    icon: Gift,
  },
  trial: {
    classes: "rounded-8 bg-tint-white-10 px-xs py-[4px] text-content-inverse",
  },
};

export const Pill = ({
  tone,
  children,
}: {
  tone: keyof typeof PILL_TONES;
  children: ReactNode;
}) => {
  const { classes, icon: Icon } = PILL_TONES[tone];

  return (
    <Text
      as="span"
      variant="paragraph-sm"
      tone="inherit"
      className={cn("flex w-fit items-center gap-[2px]", classes)}
    >
      {Icon === undefined ? null : (
        <Icon
          aria-hidden="true"
          className="h-[18px] w-[18px] shrink-0"
          strokeWidth={1.5}
        />
      )}
      {children}
    </Text>
  );
};
